import { useCallback } from 'react';
import PropTypes from 'prop-types';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { includes, isEmpty } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';

import CollapsibleTable from 'components/atoms/GefMui/CollapsibleTable';
import PaginationRow from 'components/atoms/GefMui/PaginationRow';
import SortCell from 'components/atoms/GefMui/SortCell';
import TableHasNoResults from 'components/atoms/GefMui/TableHasNoResults';
import codelistProvider from 'components/decorators/codelistProvider';
import { codelists } from 'constants/codelists';
import fetch from 'features/common/fetch';
import { sortRecords } from 'features/views/campaignDashboards/campaignDetail/actions';
import { getContactDataColumns, getDashboardsSort } from 'features/views/campaignDashboards/campaignDetail/selectors';
import { loadMore, loadPage } from 'features/views/contacts/actions';
import { getContacts } from 'features/views/contacts/selectors';

import ContactsTableRow from './ContactsTableRow';

export const ContactsTable = ({ view, campaign, solutionStates }) => {
	const dispatch = useDispatch();

	const contacts = useSelector(getContacts(view));
	const campaignContactDataColumns = useSelector(getContactDataColumns);
	const showContractEndDateColumnHeader = includes('POLICY_FINISH_DATE', campaignContactDataColumns);
	const sort = useSelector(getDashboardsSort);

	const handleLoadMore = useCallback(() => {
		dispatch(loadMore(view)(true));
	}, [dispatch, view]);

	const handlePaginationClick = useCallback(
		(e, page) => {
			dispatch(loadPage(view)(page - 1));
		},
		[dispatch, view],
	);

	const handleSort = useCallback(
		(sort) => {
			dispatch(sortRecords(view, sort));
		},
		[dispatch, view],
	);

	const fetchStorePath = `${fetch.constants.urls.contactList}.${view}`;

	const hasRecords = !isEmpty(contacts || []);

	return (
		<TableContainer>
			<CollapsibleTable
				size="small"
				tableId="ContactsTableComponent"
				autoCloseOtherRows
				tableHeader={
					<TableHead>
						<TableRow>
							<TableCell>
								<div>{getLabelMessage('dashboardManager:client')}</div>
								<div>{getLabelMessage('dashboardManager:birthDate')}</div>
							</TableCell>
							<TableCell>{getLabelMessage('dashboardManager:phone')}</TableCell>
							<TableCell>
								<div>{getLabelMessage('dashboardManager:product')}</div>
								<div>{getLabelMessage('dashboardManager:policyNo')}</div>
							</TableCell>
							<TableCell>{getLabelMessage('dashboardManager:policyAdmin')}</TableCell>
							{showContractEndDateColumnHeader && (
								<TableCell>
									<SortCell
										hasRecords={hasRecords}
										sortBy="contractEndDate"
										sort={sort}
										onSort={handleSort}
									>
										{getLabelMessage('dashboardManager:policyFinishDate')}
									</SortCell>
								</TableCell>
							)}
							<TableCell>{getLabelMessage('dashboardManager:state')}</TableCell>
							<TableCell />
							<TableCell />
							<TableCell />
						</TableRow>
					</TableHead>
				}
				tableBody={
					!hasRecords ? (
						<TableHasNoResults />
					) : (
						contacts.map((contact) => (
							<ContactsTableRow
								key={contact.id}
								campaign={campaign}
								id={contact.id}
								contact={contact}
								solutionStates={solutionStates}
								campaignContactDataColumns={campaignContactDataColumns}
							/>
						))
					)
				}
			>
				<PaginationRow
					fetchStorePath={fetchStorePath}
					colSpan={100}
					onLoadNext={handleLoadMore}
					onPaginationClick={handlePaginationClick}
				/>
			</CollapsibleTable>
		</TableContainer>
	);
};

ContactsTable.propTypes = {
	campaign: PropTypes.object,
	solutionStates: PropTypes.array,
	view: PropTypes.string,
};

export default codelistProvider(codelists.solutionState, 'solutionStates')(ContactsTable);
